import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ManualIdSchema } from "./manual.contract.js";
/**
 * The read-only contract the Web Editor Unity (`XRCreator_Web_Neu`) calls (ADR-059 D2). RED ZONE: a public Unity contract —
 * a Unity build ships separately, so a breaking change goes to `/unity/v2`, never into these shapes.
 *
 * Shaped for Unity's `JsonUtility`, which cannot read `null` into a number or an object: there is **no null** anywhere.
 * "None" is `0` for an id, `""` for a string, and an EMPTY ARRAY for an optional object (`sceneCenter`, `file3d`, …).
 *
 * Every route is scoped to ONE manual and gated by view rights on that requested manual (the same gate as `getManualBody`);
 * content resolves through the content owner (`parentManualId ?? id`, ADR-020), the facility is the requested manual's own.
 * WRITES (ADR-059 D3/D3a) are only for a STANDALONE Unity (editor play mode): it holds the manual's edit lock itself and
 * every write carries its `{clientSessionId, leaseId}`, refused unless that session holds the live lease. An EMBEDDED Unity
 * never calls a write route — the host writes for it (one writer per session). Writes are POST/PATCH only (no DELETE with a
 * body, which `UnityWebRequest` handles badly).
 */
const c = initContract();
const Unauthorized = z.object({ message: z.string() });
const Forbidden = z.object({ message: z.string(), code: z.literal("MANUAL_FORBIDDEN") });
const NotFound = z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "ELEMENT_NOT_FOUND", "FILE_NOT_FOUND"]) });
const Id = z.number().int();
/** Every write error carries a machine `code` (`MANUAL_LOCK_LOST`, `MANUAL_BODY_STALE`, `MANUAL_LOCKED`, …). */
const WriteError = z.object({ message: z.string(), code: z.string() });
const writeErrors = { 400: WriteError, 401: Unauthorized, 403: WriteError, 404: WriteError, 409: WriteError, 422: WriteError };
/** `[x, y, z]`; Unity writes it back as its legacy `"x;y;z"`. */
const Vec3 = z.tuple([z.number(), z.number(), z.number()]);
/** A ready file with a short-lived signed GET link (900 s). Unity downloads from `url`; refresh through `getFile`. */
export const UnityFileSchema = z.object({
    id: Id,
    name: z.string(),
    /** Bytes; 0 when unknown. */
    size: z.number().int(),
    /** "" when storage is not configured (tests). */
    url: z.string(),
});
export const UnitySettingSchema = z.object({
    /** The `ar_element_setting` row id. */
    id: Id,
    /** The setting definition id (`setting.id`). */
    settingId: Id,
    name: z.string(),
    /** Raw, as stored (often JSON itself); "" when empty. */
    value: z.string(),
    /** The bound sensor (ADR-037), 0 when none. */
    sensorId: Id,
    files: z.array(UnityFileSchema),
});
export const UnityElementSchema = z.object({
    id: Id,
    name: z.string(),
    displayName: z.string(),
    /** 0 for a SceneCenter without lineage. */
    blueprintId: Id,
    /** 0 when the element has no parent. */
    parentId: Id,
    /** The step it belongs to; 0 for an all-steps element and for the SceneCenter. */
    stepId: Id,
    category: z.string(),
    isPlaceable: z.boolean(),
    hasBeenPositioned: z.boolean(),
    position: Vec3,
    rotation: Vec3,
    size: Vec3,
    /** Every setting except `Section`, `Step`, `SectionsAndSteps` (Unity synthesizes those, ADR-059 D1), in creation order. */
    settings: z.array(UnitySettingSchema),
});
export const UnityStepSchema = z.object({ id: Id, name: z.string(), mainElementId: Id });
export const UnitySectionSchema = z.object({ id: Id, name: z.string(), steps: z.array(UnityStepSchema) });
export const UnityManualSceneSchema = z.object({
    manual: z.object({
        id: Id,
        name: z.string(),
        version: z.number().int(),
        /** The REQUESTED manual's facility. */
        facilityId: Id,
        /** The content owner (the parent of a linked child, else the manual itself). */
        contentManualId: Id,
        isSensorManual: z.boolean(),
        /** A linked child (ADR-020): it shows its parent's content and is read-only; the host refuses every request on it. */
        isLinkedCopy: z.boolean(),
    }),
    /** In order; names exactly as stored, so name references in setting values resolve. */
    sections: z.array(UnitySectionSchema),
    /** The manual-level settings carrier: zero or one element. */
    sceneCenter: z.array(UnityElementSchema),
    /** Step and all-steps elements, bound exactly as the editor binds them (ADR-059 D1). */
    elements: z.array(UnityElementSchema),
});
export const UnityFacilitySchema = z.object({
    id: Id,
    /** The legacy 3D-scene blob, VERBATIM as a JSON string ("" when none) — Unity parses it with its own `Data3D`. */
    data3d: z.string(),
    /** Zero or one file each. */
    file3d: z.array(UnityFileSchema),
    assetBundleWebgl: z.array(UnityFileSchema),
});
export const UnityBlueprintSchema = z.object({
    id: Id,
    name: z.string(),
    displayName: z.string(),
    categoryId: Id,
    category: z.string(),
    isPlaceable: z.boolean(),
    /** "" when there is no ready icon. */
    iconUrl: z.string(),
});
export const UnityCatalogSchema = z.object({
    blueprints: z.array(UnityBlueprintSchema),
    /** The setting dictionary: name → `settingId`. */
    settings: z.array(z.object({ id: Id, name: z.string() })),
});
const FileIdSchema = z.coerce.number().int().positive().max(2147483647);
/** A Unity session id: always `unity-…`, so the editor's routes can tell a Unity lease apart (ADR-059 D3a). */
export const UnityClientSessionIdSchema = z.string().regex(/^unity-[A-Za-z0-9._-]{8,120}$/);
/** The lease every Unity write presents. */
export const UnityLeaseSchema = z.object({ clientSessionId: UnityClientSessionIdSchema, leaseId: z.string().uuid() });
export const UnityWriteSchema = UnityLeaseSchema.extend({ expectedVersion: z.number().int().min(0).max(2147483647) });
/** The lock without nulls: "" for none, `status` as in the editor's lock contract. */
export const UnityLockStateSchema = z.object({
    status: z.enum(["unlocked", "held_by_me", "held_by_other"]),
    holderName: z.string(),
    expiresAt: z.string(),
    leaseId: z.string(),
    heartbeatSeconds: z.number().int(),
    waitingName: z.string(),
    handoverDeadline: z.string(),
});
/** A write's result: the manual's new version and the written element as the scene shows it. */
export const UnityWriteResultSchema = z.object({ version: z.number().int(), element: UnityElementSchema });
export const unityContract = c.router({
    getUnityScene: {
        method: "GET",
        path: "/unity/v1/manuals/:id/scene",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: { 200: UnityManualSceneSchema, 401: Unauthorized, 403: Forbidden, 404: NotFound },
        summary: "Unity: the manual's sections, SceneCenter and elements with all settings (ADR-059; view gate on the requested manual).",
    },
    getUnityElement: {
        method: "GET",
        path: "/unity/v1/manuals/:id/elements/:elementId",
        pathParams: z.object({ id: ManualIdSchema, elementId: FileIdSchema }),
        responses: { 200: UnityElementSchema, 401: Unauthorized, 403: Forbidden, 404: NotFound },
        summary: "Unity: one element of the manual's scene (404 for an element the scene does not show).",
    },
    getUnityFacility: {
        method: "GET",
        path: "/unity/v1/manuals/:id/facility",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: { 200: UnityFacilitySchema, 401: Unauthorized, 403: Forbidden, 404: NotFound },
        summary: "Unity: the requested manual's facility — data3D, 3D file, WebGL asset bundle.",
    },
    getUnityCatalog: {
        method: "GET",
        path: "/unity/v1/manuals/:id/catalog",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: { 200: UnityCatalogSchema, 401: Unauthorized, 403: Forbidden, 404: NotFound },
        summary: "Unity: the realm's blueprints and the setting dictionary.",
    },
    getUnityFile: {
        method: "GET",
        path: "/unity/v1/manuals/:id/files/:fileId",
        pathParams: z.object({ id: ManualIdSchema, fileId: FileIdSchema }),
        responses: { 200: UnityFileSchema, 401: Unauthorized, 403: Forbidden, 404: NotFound },
        summary: "Unity: a fresh signed link for a ready file THIS manual references (element settings, facility 3D file or bundle, a blueprint icon); 404 otherwise.",
    },
    // ── Writes, STANDALONE only (ADR-059 D3a) ─────────────────────────────────────────────────────────────────────────
    acquireUnityLock: {
        method: "POST",
        path: "/unity/v1/manuals/:id/lock",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({ clientSessionId: UnityClientSessionIdSchema }),
        responses: { 200: UnityLockStateSchema, ...writeErrors },
        summary: "Unity: take the manual's edit lease (409 MANUAL_LOCKED when a browser or another session holds it).",
    },
    heartbeatUnityLock: {
        method: "POST",
        path: "/unity/v1/manuals/:id/lock/heartbeat",
        pathParams: z.object({ id: ManualIdSchema }),
        body: UnityLeaseSchema.extend({ active: z.boolean() }),
        responses: { 200: UnityLockStateSchema, ...writeErrors },
        summary: "Unity: renew the lease; `active` while the user works or a write is in flight (409 MANUAL_LOCK_LOST).",
    },
    handoverUnityLock: {
        method: "POST",
        path: "/unity/v1/manuals/:id/lock/handover",
        pathParams: z.object({ id: ManualIdSchema }),
        body: UnityLeaseSchema,
        responses: { 200: UnityLockStateSchema, ...writeErrors },
        summary: "Unity: hand the lease to the waiting requester.",
    },
    requestUnityLock: {
        method: "POST",
        path: "/unity/v1/manuals/:id/lock/request",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({ clientSessionId: UnityClientSessionIdSchema }),
        responses: { 200: UnityLockStateSchema, ...writeErrors },
        summary: "Unity: ask the current holder to hand over (request to edit).",
    },
    releaseUnityLock: {
        method: "POST",
        path: "/unity/v1/manuals/:id/lock/release",
        pathParams: z.object({ id: ManualIdSchema }),
        body: UnityLeaseSchema,
        responses: { 200: UnityLockStateSchema, ...writeErrors },
        summary: "Unity: give the lease up (play mode ends).",
    },
    setUnityElementTransform: {
        method: "PATCH",
        path: "/unity/v1/manuals/:id/elements/:elementId/transform",
        pathParams: z.object({ id: ManualIdSchema, elementId: FileIdSchema }),
        body: UnityWriteSchema.extend({ position: Vec3, rotation: Vec3, size: Vec3 }),
        responses: { 200: UnityWriteResultSchema, ...writeErrors },
        summary: "Unity: save an element's transform (sets hasBeenPositioned).",
    },
    copyUnityElement: {
        method: "POST",
        path: "/unity/v1/manuals/:id/elements/copy",
        pathParams: z.object({ id: ManualIdSchema }),
        body: UnityWriteSchema.extend({
            /** The manual the copied element is in (this one for a duplicate, another for a paste across manuals). */
            sourceManualId: FileIdSchema,
            sourceElementId: FileIdSchema,
            /** The target step; 0 = the all-steps set. */
            stepId: z.number().int().min(0).max(2147483647),
            position: Vec3,
            rotation: Vec3,
            size: Vec3,
        }),
        responses: { 200: UnityWriteResultSchema, ...writeErrors },
        summary: "Unity: duplicate or paste — a server-side deep copy of an element into a step, with the scope rules of ADR-059 D3a.",
    },
    deleteUnityElement: {
        method: "POST",
        path: "/unity/v1/manuals/:id/elements/:elementId/delete",
        pathParams: z.object({ id: ManualIdSchema, elementId: FileIdSchema }),
        body: UnityWriteSchema,
        responses: { 200: z.object({ version: z.number().int() }), ...writeErrors },
        summary: "Unity: delete a non-main element.",
    },
    setUnityElementSetting: {
        method: "PATCH",
        path: "/unity/v1/manuals/:id/elements/:elementId/settings/:name",
        pathParams: z.object({ id: ManualIdSchema, elementId: FileIdSchema, name: z.string().min(1).max(200) }),
        body: UnityWriteSchema.extend({ value: z.string().max(100000) }),
        responses: { 200: UnityWriteResultSchema, ...writeErrors },
        summary: "Unity: write one setting value (the editor's allow-list and validators apply; Unity writes Path).",
    },
    setUnityFacilityData3d: {
        method: "PATCH",
        path: "/unity/v1/manuals/:id/facility",
        pathParams: z.object({ id: ManualIdSchema }),
        body: UnityLeaseSchema.extend({ data3d: z.string().max(1000000) }),
        responses: { 200: z.object({ data3d: z.string() }), ...writeErrors },
        summary: "Unity: overwrite the manual's facility data3D (needs `manage` on the facility and the manual's lease).",
    },
});
//# sourceMappingURL=unity.contract.js.map